import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/UI/dialog';
import Badge from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';

function TradeChart({ data }) {
  const { pricePath, pricePath2, markers, levels } = data;

  return (
    <svg viewBox="0 0 400 220" className="w-full h-auto">
      {/* Background grid */}
      <defs>
        <pattern id="chartGrid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path
            d="M 40 0 L 0 0 0 40"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.06"
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="400" height="220" fill="url(#chartGrid)" />

      {/* Key levels */}
      {levels.map((level, i) => (
        <g key={i}>
          <line
            x1="0" y1={level.y} x2="400" y2={level.y}
            stroke="currentColor" strokeOpacity="0.3"
            strokeDasharray="6 4" strokeWidth="1"
          />
          <text x="4" y={level.y - 5} fill="currentColor" opacity="0.45" fontSize="10">
            {level.label}
          </text>
        </g>
      ))}

      {/* Secondary price line (arbitrage) */}
      {pricePath2 && (
        <path
          d={pricePath2}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="1.5"
          strokeOpacity="0.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="4 3"
        />
      )}

      {/* Main price line */}
      <path
        d={pricePath}
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Entry / exit markers */}
      {markers.map((m, i) => (
        <g key={i} transform={`translate(${m.x}, ${m.y})`}>
          {m.type === 'buy' ? (
            <>
              <circle r="11" fill="#22c55e" opacity="0.15" />
              <circle r="7" fill="#22c55e" opacity="0.9" />
              <path d="M 0,-3.5 L 3.5,2 L -3.5,2 Z" fill="white" />
              <text y="22" textAnchor="middle" fill="#22c55e" fontSize="9" fontWeight="bold">
                BUY
              </text>
            </>
          ) : (
            <>
              <circle r="11" fill="#ef4444" opacity="0.15" />
              <circle r="7" fill="#ef4444" opacity="0.9" />
              <path d="M 0,3.5 L 3.5,-2 L -3.5,-2 Z" fill="white" />
              <text y="22" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="bold">
                SELL
              </text>
            </>
          )}
        </g>
      ))}
    </svg>
  );
}

export default function StrategyDetailModal({ strategy, open, onClose, onSelect }) {
  if (!strategy) return null;

  const { name, description, icon: Icon, detailData } = strategy;
  const { howItWorks, bestMarkets, tradeFlow } = detailData;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle>{name}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* How It Works */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">How It Works</h4>
          <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
            {howItWorks.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>
        </div>

        {/* Best Markets */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">Best Markets</h4>
          <div className="flex flex-wrap gap-2">
            {bestMarkets.map((market, i) => (
              <Badge key={i} variant="info">{market}</Badge>
            ))}
          </div>
        </div>

        {/* Trade Execution */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground">Trade Execution</h4>
          <div className="rounded-lg border border-border bg-card/50 p-2">
            <TradeChart data={tradeFlow} />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onSelect(strategy)} size="lg" className="w-full sm:w-auto">
            Select Strategy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
